import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';
import {
  AgencyType,
  IAccount,
  IMobileAppSettings,
  IUser,
  IUserReq,
  UserStatus,
} from '@/models';
import { IDevice } from '@/models/device';

import { http } from './http';
import { IGetParams, IListResponse, IPaginatedListResponse } from './types';

export const UserApi = {
  get: async (params: IGetParams): Promise<AxiosResponse<IListResponse>> => {
    return http.get(`api/${ApiVersion.V1}/users/`, { params });
  },
  list: async (
    params: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse>> => {
    return http.get(`api/${ApiVersion.V1}/users/list`, { params });
  },
  getOptionsByAgencyType: async (
    agencyType: AgencyType,
  ): Promise<AxiosResponse<IUser[]>> => {
    return http.get(
      `api/${ApiVersion.V1}/users/options?agencyType=${agencyType}`,
    );
  },
  getUserDevices: (
    userId: string,
    params: IGetParams,
  ): Promise<AxiosResponse<IListResponse<IDevice>>> => {
    return http.get(`api/${ApiVersion.V1}/users/${userId}/devices`, { params });
  },
  getOne: async (id: string): Promise<AxiosResponse<IUser>> => {
    return http.get(`api/${ApiVersion.V1}/users/${id}`);
  },
  getNearByUsersFromHQ: async (): Promise<AxiosResponse<IUser[]>> => {
    return http.get(`api/${ApiVersion.V1}/users/nearby-from-hq`);
  },
  create: async (data: IUserReq): Promise<AxiosResponse<IUser>> => {
    return http.post(`api/${ApiVersion.V1}/users/`, data);
  },
  update: async ({ _id, ...data }: IUserReq): Promise<AxiosResponse<IUser>> => {
    return http.put(`api/${ApiVersion.V1}/users/${_id}`, data);
  },
  updateMe: async (
    data: Partial<{
      firstName: string;
      middleName: string | null;
      lastName: string;
      phoneNumber: string | null;
      profile: {
        emergencyContact: {
          name?: string | null;
          phoneNumber?: string | null;
        };
      };
    }>,
  ): Promise<AxiosResponse<IUser>> => {
    return http.put(`api/${ApiVersion.V1}/users/me`, data);
  },
  getCurrentUser: async (): Promise<AxiosResponse<IUser>> => {
    return http.get(`api/${ApiVersion.V2}/users/me`);
  },
  getMyAccount: async (): Promise<AxiosResponse<IAccount>> => {
    return http.get(`api/${ApiVersion.V1}/accounts/me`);
  },
  sendVerificationEmail: async (userId: string): Promise<AxiosResponse> => {
    return http.post(
      `api/${ApiVersion.V1}/users/${userId}/send-verification-email`,
    );
  },
  toggleLoginStatus: async (id: string): Promise<AxiosResponse<IUser>> => {
    return http.patch(`api/${ApiVersion.V1}/users/${id}/login-status`);
  },
  updateRoles: async ({
    id,
    ...data
  }: {
    id: string;
    roles: {
      role: string;
      agency?: string;
    }[];
  }): Promise<AxiosResponse<IAccount>> => {
    return http.patch(`api/${ApiVersion.V1}/users/${id}/roles`, data);
  },
  getAblyToken: async (): Promise<AxiosResponse> => {
    return http.post(`api/${ApiVersion.V1}/users/ably-token-request`);
  },
  getMobileAppSettings: async (
    userId: string,
  ): Promise<AxiosResponse<IMobileAppSettings>> => {
    return http.get(`api/${ApiVersion.V1}/users/${userId}/mobile-app-settings`);
  },

  updateMobileAppSettings: async (
    userId: string,
    settings: IMobileAppSettings,
  ): Promise<AxiosResponse<IUser>> => {
    return http.put(
      `api/${ApiVersion.V1}/users/${userId}/mobile-app-settings`,
      settings,
    );
  },

  updateMapLayersPreferences: async (
    userId: string,
    settings: Record<string, any>,
  ): Promise<AxiosResponse<IUser>> => {
    return http.patch(`api/${ApiVersion.V1}/users/${userId}/map-layers`, {
      settings,
    });
  },

  updateLastSeenReleaseVersion: async (
    userId: string,
    lastSeenReleaseVersion: string,
  ): Promise<AxiosResponse<IUser>> => {
    return http.patch(`api/${ApiVersion.V1}/users/${userId}/seen-version`, {
      lastSeenReleaseVersion,
    });
  },
  checkDeviceNcic: async (
    oriNumber?: string,
    mnemonic?: string,
  ): Promise<AxiosResponse<{ oriNumber: boolean; mnemonic: boolean }>> => {
    return http.get(`api/${ApiVersion.V1}/devices/ncic-check`, {
      params: {
        oriNumber,
        mnemonic,
      },
    });
  },
  updateDeviceNcic: async (
    deviceId: string,
    oriNumber?: string,
    mnemonic?: string,
  ): Promise<AxiosResponse<IDevice>> => {
    return http.patch(`api/${ApiVersion.V1}/devices/${deviceId}/ncic`, {
      oriNumber,
      mnemonic,
    });
  },
  changeUserStatus: (
    userId: string,
    status: UserStatus,
    reason: string,
  ): Promise<AxiosResponse<IUser>> => {
    return http.patch(`api/${ApiVersion.V1}/users/${userId}/status`, {
      status,
      reason,
    });
  },
  toggleNcic: (deviceId: string): Promise<AxiosResponse<IDevice>> => {
    return http.patch(`api/${ApiVersion.V1}/devices/${deviceId}/ncic-toggle`);
  },
  restrictDevice: async (
    deviceId: string,
    isRestricted: boolean,
  ): Promise<AxiosResponse<IDevice>> => {
    return http.patch(`api/${ApiVersion.V1}/devices/${deviceId}/restrict`, {
      isRestricted,
    });
  },

  updateDeviceRestriction: async (
    userId: string,
    isNewDevicesRestricted: boolean,
  ): Promise<AxiosResponse<IUser>> => {
    return http.patch(
      `api/${ApiVersion.V1}/users/${userId}/device-restriction`,
      {
        isLockedFromNewDevices: isNewDevicesRestricted,
      },
    );
  },
};
